import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import {
  User, ShoppingBag, Package, Settings, Store, ChevronRight, LogOut, QrCode,
} from 'lucide-react-native'
import { CustomerQRDisplay } from '../components/CustomerQR'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import {
  Button, ButtonText, Card, Text, ListItem, Avatar,
  YStack, XStack, Sheet,
} from '@fliisi/pollen'
import { ScreenLayout } from '../components/ScreenLayout'


export default function BuyerProfileScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { user, session } = useAuth()
  const { t } = useLanguage()
  const isLoggedIn = !!session
  const [hasStores, setHasStores] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [showQR, setShowQR] = useState(false)

  useEffect(() => {
    if (user?.id) {
      supabase
        .from('flea_market_staff')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .then(({ count }) => setHasStores((count || 0) > 0))
      supabase
        .from('users')
        .select('avatar_url')
        .eq('id', user.id)
        .single()
        .then(({ data }) => setAvatarUrl(data?.avatar_url ?? null))
    }
  }, [user?.id])

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || t('user_default')
  const initials = displayName.slice(0, 2).toUpperCase()

  type MenuItem = {
    key: string
    icon: React.ComponentType<{ size: number; color: string }>
    label: string
    route?: keyof RootStackParamList
    merchantOnly?: boolean
  }

  const menuItems: MenuItem[] = [
    { key: 'products', icon: Package, label: t('my_products'), route: 'Home' },
    { key: 'purchases', icon: ShoppingBag, label: t('my_purchases') },
    { key: 'settings', icon: Settings, label: t('profile_settings'), route: 'ProfileSettings' },
    { key: 'merchant', icon: Store, label: t('merchant_portal'), route: 'MerchantStoreSelect', merchantOnly: true },
  ]

  const visibleMenuItems = menuItems.filter(item => !item.merchantOnly || hasStores)

  const handleMenuPress = (item: MenuItem) => {
    if (!isLoggedIn) { navigation.navigate('Login'); return }
    if (item.key === 'purchases') { navigation.navigate('PurchaseHistory' as any); return }
    if (item.route) navigation.navigate(item.route as any)
  }

  return (
    <ScreenLayout>
      {/* Profile Header */}
      <YStack pt={Platform.OS === 'web' ? 20 : 56} pb="$xl">
        <XStack ai="center" gap="$md">
          {isLoggedIn ? (
            <Avatar size="lg" src={avatarUrl} initials={initials} />
          ) : (
            <YStack w={64} h={64} br={16} bg="rgba(116,140,112,0.13)" jc="center" ai="center">
              <User size={28} color="#748C70" />
            </YStack>
          )}
          <YStack f={1}>
            <XStack ai="center" gap="$sm">
              <Text preset="h3">{isLoggedIn ? displayName : t('sign_in_full')}</Text>
              {isLoggedIn && (
                <XStack
                  ai="center" gap={4} px={8} py={4} br="$full"
                  bg="rgba(116,140,112,0.1)"
                  onPress={() => setShowQR(true)}
                  pressStyle={{ opacity: 0.7 }}
                  cursor="pointer"
                >
                  <QrCode size={13} color="#748C70" />
                  <Text preset="caption" color="$colorPrimary">QR</Text>
                </XStack>
              )}
            </XStack>
            <Text preset="bodySmall" tone="secondary" mt={2}>
              {isLoggedIn ? user?.email : t('save_favorites_track_purchases')}
            </Text>
          </YStack>
        </XStack>
      </YStack>

      <Sheet open={showQR} onOpenChange={setShowQR} snapPoints={[55]} noHandle={false}>
        <YStack px="$md" pt="$sm" pb="$xl" ai="center">
          <CustomerQRDisplay size={220} />
        </YStack>
      </Sheet>

      {/* Auth buttons (logged out) */}
      {!isLoggedIn && (
        <YStack gap={10} mb="$xl">
          <Button variant="primary" onPress={() => navigation.navigate('Login')}>
            <ButtonText>{t('sign_in_full')}</ButtonText>
          </Button>
          <Button variant="secondary" onPress={() => navigation.navigate('Login')}>
            <ButtonText variant="secondary">{t('create_account')}</ButtonText>
          </Button>
        </YStack>
      )}

      {/* Menu items */}
      <YStack gap="$sm" mb="$xl">
        {visibleMenuItems.map(item => {
          const Icon = item.icon
          const disabled = !isLoggedIn
          return (
            <ListItem
              key={item.key}
              icon={
                <YStack w={40} h={40} br={12} bg={disabled ? '#E0E2DB' : '#F2F0E9'} jc="center" ai="center">
                  <Icon size={20} color={disabled ? '#858B80' : '#3E423A'} />
                </YStack>
              }
              title={item.label}
              right={<ChevronRight size={16} color={disabled ? '#E0E2DB' : '#858B80'} />}
              onPress={() => handleMenuPress(item)}
              opacity={disabled ? 0.5 : 1}
              bg="$backgroundCard"
              br="$xl"
              p="$md"
            />
          )
        })}
      </YStack>

      {/* Sign out */}
      {isLoggedIn && (
        <YStack mb="$xl">
          <Button
            variant="destructiveoutline"
            onPress={async () => {
              await supabase.auth.signOut()
              navigation.reset({ index: 0, routes: [{ name: 'Landing' }] })
            }}
          >
            <XStack ai="center" gap="$sm">
              <LogOut size={18} color="#D32F2F" />
              <ButtonText variant="destructiveoutline">{t('sign_out')}</ButtonText>
            </XStack>
          </Button>
        </YStack>
      )}

      {/* Info card */}
      <Card variant="flat" bg="rgba(232,180,156,0.12)" p="$lg">
        <Text preset="label" mb="$xs">{t('about_fliisi')}</Text>
        <Text preset="bodySmall" tone="secondary" lineHeight={20}>
          {t('fliisi_description')}
        </Text>
      </Card>

      <YStack h={32} />
    </ScreenLayout>
  )
}
